// AdminMassDeleteHub — global admin tool (sidebar entry). Lets an admin pick
// any whitelisted collection, see the current count, and wipe ALL rows after
// the 2-step typed confirmation. Provides one place that covers every module,
// independent of which page the admin is on.
import { useEffect, useState } from "react";
import { ShieldAlert, RefreshCw, Trash2, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import MassDeleteConfirm from "./MassDeleteConfirm";

function isAdmin(user) {
  if (!user) return false;
  return (user.role || "").toLowerCase() === "admin" || user.is_super_admin;
}

export default function AdminMassDeleteHub({ user, open, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState(null); // {key,label,count}

  async function load() {
    if (!isAdmin(user)) return;
    setLoading(true);
    try {
      const { data } = await api.get("/admin/bulk-delete/collections");
      setRows(data.collections || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte Liste nicht laden");
    } finally { setLoading(false); }
  }

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  if (!isAdmin(user) || !open) return null;

  async function doWipe() {
    if (!target) return;
    try {
      const { data } = await api.post("/admin/bulk-delete-all", {
        collection: target.key,
        expected_count: target.count,
        confirm: "LOESCHEN",
      });
      toast.success(`${data.deleted} Einträge aus „${target.label}" gelöscht`);
      setTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschung fehlgeschlagen");
    }
  }

  const totalRows = rows.reduce((s, r) => s + (r.count || 0), 0);

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="admin-mass-delete-hub">
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
          <header className="px-5 py-4 border-b border-zinc-800 flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-rose-400" />
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-wider text-rose-300 font-semibold">Admin-Werkzeug</div>
              <h2 className="font-display font-bold text-zinc-50">Massen-Löschung</h2>
            </div>
            <button onClick={load} disabled={loading}
              data-testid="admin-mass-delete-refresh"
              className="p-1.5 text-zinc-500 hover:text-zinc-200 rounded">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </button>
            <button onClick={onClose} data-testid="admin-mass-delete-close"
              className="p-1.5 text-zinc-500 hover:text-zinc-200 rounded">
              <X className="w-4 h-4" />
            </button>
          </header>

          <div className="px-5 py-3 border-b border-zinc-800 text-xs text-zinc-400">
            Wähle ein Modul aus. Die Löschung betrifft <span className="text-zinc-200 font-semibold">nur den aktuellen Tenant</span>, kann aber <span className="text-rose-300 font-semibold">nicht rückgängig gemacht werden</span>. Stammdaten (Mitarbeiter, Fahrzeuge, Abteilungen, Einstellungen) sind nicht in der Liste.
          </div>

          <div className="flex-1 overflow-y-auto divide-y divide-zinc-800">
            {rows.length === 0 && !loading && (
              <div className="p-8 text-center text-sm text-zinc-500">Keine Daten</div>
            )}
            {rows.map(r => (
              <div key={r.key} data-testid={`admin-mass-delete-row-${r.key}`}
                className="px-5 py-2.5 flex items-center gap-3 hover:bg-zinc-800/30">
                <div className="flex-1">
                  <div className="text-sm text-zinc-100">{r.label}</div>
                  <div className="text-[10px] text-zinc-500 font-mono">{r.key}</div>
                </div>
                <div className="text-sm tabular-nums text-zinc-300 w-16 text-right">
                  {r.count.toLocaleString("de-AT")}
                </div>
                <button
                  onClick={() => setTarget(r)}
                  disabled={r.count === 0}
                  data-testid={`admin-mass-delete-trigger-${r.key}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 disabled:opacity-30 disabled:cursor-not-allowed rounded">
                  <Trash2 className="w-3 h-3" /> Leeren
                </button>
              </div>
            ))}
          </div>

          <footer className="px-5 py-3 border-t border-zinc-800 text-[11px] text-zinc-500 flex items-center justify-between">
            <span>Summe in der Liste: <span className="text-zinc-300 font-semibold">{totalRows.toLocaleString("de-AT")}</span> Datensätze</span>
            <span>Stammdaten bleiben unangetastet</span>
          </footer>
        </div>
      </div>

      <MassDeleteConfirm
        open={!!target}
        count={target?.count || 0}
        label={`Modul „${target?.label}" leeren`}
        onCancel={() => setTarget(null)}
        onConfirm={doWipe}
      />
    </>
  );
}
